import { useMemo } from 'react';

import { useExtractAutomationCodeConnections } from '@/generated/hooks/grimoire/v1/api';
import type { V1CredentialInfo } from '@/generated/hooks/grimoire/models';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { FilterBuilder } from '@/shared/utils';

/**
 * Stable empty map. `ReadonlyMap` because it is a singleton handed straight
 * to callers — one caller seeding a default would poison every other.
 */
const NO_CREDENTIALS: ReadonlyMap<string, V1CredentialInfo> = new Map();

/** Params for {@link useAutomationCredentials}. */
interface IUseAutomationCredentialsParams {
  automationId?: string;
  /** Draft connections and published ones are separate sets. */
  isDraft?: boolean;
  /** False skips the request entirely and returns the stable empty map. */
  enabled?: boolean;
}

/**
 * An automation's connections, keyed by the alias its code calls them by.
 *
 * The alias is all an external call records, so anything that wants the book
 * behind one (its name, version or icon) resolves it through here. See
 * `resolveStepConnections` for the parsing.
 */
export function useAutomationCredentials({
  automationId,
  isDraft,
  enabled = true,
}: IUseAutomationCredentialsParams): ReadonlyMap<string, V1CredentialInfo> {
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const { data } = useExtractAutomationCodeConnections(
    organizationId,
    workspaceId,
    automationId ?? '',
    {
      filter: FilterBuilder.create()
        .equals('stage', isDraft ? 'DRAFT' : 'PUBLISHED')
        .build(),
    },
    { query: { enabled: enabled && Boolean(automationId) } },
  );

  return useMemo(() => {
    const connections = data?.data?.connections ?? [];
    if (connections.length === 0) return NO_CREDENTIALS;
    return new Map<string, V1CredentialInfo>(
      connections
        .filter(
          (
            connection,
          ): connection is V1CredentialInfo & { connection_alias: string } =>
            Boolean(connection.connection_alias),
        )
        .map(connection => [connection.connection_alias, connection]),
    );
  }, [data]);
}
